// @ts-nocheck
import React, { useEffect } from 'react';
import ReactApexChart from 'react-apexcharts';
import {
  Grid,
  Card,
  CardHeader,
  Typography,
  Box,
  Stack,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Tooltip,
  TextField,
  CircularProgress,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import Iconify from '../iconify';
import { useChart } from '../chart';
import { fShortenNumber } from '../../utils/formatNumber';
import FeedbackOverviewCard from './FeedbackOverviewCard';
import { useFetchUserStatistics } from '../../hooks/KnowledgeAI/useDashboardApi';

interface UserDetailsPageProps {
  username: string;
  onBack: () => void;
  contextLabel?: string;
  onFeedbackClick?: (feedbackType: string) => void;
}

export default function UserDetailsPage({ username, onBack, contextLabel, onFeedbackClick }: UserDetailsPageProps) {
  const theme = useTheme();
  const { data: apiData, loading, fetchUserStatistics } = useFetchUserStatistics();

  // Fetch user statistics when the username changes
  useEffect(() => {
    if (username) fetchUserStatistics(username);
  }, [username]); // eslint-disable-line react-hooks/exhaustive-deps

  // Use API data only
  const userDetails = apiData;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!userDetails) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 10 }}>
        <Typography variant="body1" color="text.secondary">
          No data available for this user.
        </Typography>
      </Box>
    );
  }

  const {
    totalConversations,
    totalTokens,
    totalCost,
    avgTokensPerQuery,
    avgCostPerQuery,
    feedbackGiven,
    queriesOverTime,
    recentQueries,
    quotaLimit,
    quotaEnabled,
  } = userDetails;

  const summaryStats = [
    { label: 'Conversations', value: totalConversations, icon: 'mdi:forum', color: 'info' },
    { label: 'Tokens Used', value: fShortenNumber(totalTokens), icon: 'mdi:counter', color: 'warning' },
    { label: 'Total Cost', value: `$${totalCost.toFixed(2)}`, icon: 'mdi:currency-usd', color: 'success' },
    { label: 'Avg Tokens/Query', value: fShortenNumber(avgTokensPerQuery), icon: 'mdi:chart-line', color: 'secondary' },
    { label: 'Avg Cost/Query', value: `$${avgCostPerQuery.toFixed(2)}`, icon: 'mdi:receipt', color: 'error' },
  ];

  return (
    <Box>
      {/* Header with back button */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <Button variant="outlined" startIcon={<Iconify icon="mdi:arrow-left" />} onClick={onBack} size="small">
          Back{contextLabel ? ` to ${contextLabel}` : ''}
        </Button>
      </Stack>

      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: alpha(theme.palette.primary.main, 0.12),
            color: theme.palette.primary.main,
          }}
        >
          <Iconify icon="mdi:account" width={28} />
        </Box>
        <Box>
          <Typography variant="h5">{username}</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            User Activity Details
            {quotaEnabled && quotaLimit && ` • Quota: ${fShortenNumber(quotaLimit)} tokens/month`}
          </Typography>
        </Box>
      </Stack>

      {/* Summary Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {summaryStats.map((stat) => (
          <Grid item xs={6} sm={4} md={2} key={stat.label}>
            <Card
              sx={{
                py: 2.5,
                textAlign: 'center',
                boxShadow: 0,
                color: (t) => t.palette[stat.color].darker,
                bgcolor: (t) => t.palette[stat.color].lighter,
              }}
            >
              <Iconify icon={stat.icon} width={24} sx={{ color: (t) => t.palette[stat.color].dark, mb: 0.5 }} />
              <Typography variant="h6">{stat.value}</Typography>
              <Typography variant="caption" sx={{ opacity: 0.72 }}>
                {stat.label}
              </Typography>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        {/* User Query Trend */}
        <Grid item xs={12} md={8}>
          <UserQueryTrend data={queriesOverTime} />
        </Grid>

        {/* Feedback Summary */}
        <Grid item xs={12} md={4}>
          <FeedbackOverviewCard title="Feedback Breakdown" data={feedbackGiven} onFeedbackClick={onFeedbackClick} />
        </Grid>

        {/* Recent Queries */}
        <Grid item xs={12}>
          <RecentQueriesTable queries={recentQueries} />
        </Grid>
      </Grid>
    </Box>
  );
}

// ----------- Sub-components -----------

function UserQueryTrend({ data }) {
  const chartOptions = useChart({
    labels: data.map((d) => d.date),
    xaxis: { type: 'datetime' },
    tooltip: {
      y: { formatter: (val) => `${val} queries` },
    },
    fill: {
      type: 'gradient',
      gradient: { shadeIntensity: 1, opacityFrom: 0.5, opacityTo: 0.1, stops: [0, 100] },
    },
  });

  return (
    <Card>
      <CardHeader title="Query Activity" subheader="User queries over time" />
      <Box sx={{ p: 3, pb: 1 }} dir="ltr">
        <ReactApexChart
          type="area"
          series={[{ name: 'Queries', data: data.map((d) => d.queries) }]}
          options={chartOptions}
          height={240}
        />
      </Box>
    </Card>
  );
}

function RecentQueriesTable({ queries }) {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  const [startDate, setStartDate] = React.useState('');
  const [endDate, setEndDate] = React.useState('');

  // Populate date pickers from initial data
  React.useEffect(() => {
    if (queries?.length > 0) {
      const dates = queries.map((q) => q.timestamp.slice(0, 10)).sort();
      setStartDate(dates[0]);
      setEndDate(dates[dates.length - 1]);
    }
  }, [queries]);

  // Client-side date filtering
  const filteredQueries = React.useMemo(() => {
    if (!startDate && !endDate) return queries;
    return queries.filter((q) => {
      const qDate = q.timestamp.slice(0, 10);
      if (startDate && qDate < startDate) return false;
      if (endDate && qDate > endDate) return false;
      return true;
    });
  }, [queries, startDate, endDate]);

  const handleDownload = () => {
    const headers = ['Query', 'Tokens', 'Cost', 'Time'];
    const rows = filteredQueries.map((q) => [
      `"${q.query.replace(/"/g, '""')}"`,
      q.tokens,
      `$${q.cost.toFixed(2)}`,
      new Date(q.timestamp).toLocaleString(),
    ]);
    const csv = [headers, ...rows].map((r) => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'recent_queries.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const paginatedQueries = filteredQueries.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // Reset page when filter changes
  React.useEffect(() => {
    setPage(0);
  }, [startDate, endDate]);

  return (
    <Card>
      <CardHeader
        title="Recent Queries"
        subheader={`${filteredQueries.length} queries${startDate || endDate ? ' (filtered)' : ''}`}
        action={
          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title="Download CSV">
              <IconButton onClick={handleDownload}>
                <Iconify icon="mdi:download" />
              </IconButton>
            </Tooltip>
          </Stack>
        }
      />
      {/* Date range filters below header, wraps on mobile */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} sx={{ px: 2, pb: 1.5 }}>
        <Iconify
          icon="mdi:calendar-range"
          width={20}
          sx={{ color: 'text.secondary', display: { xs: 'none', sm: 'block' } }}
        />
        <TextField
          label="From"
          type="date"
          size="small"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ width: { xs: '100%', sm: 160 } }}
        />
        <TextField
          label="To"
          type="date"
          size="small"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ width: { xs: '100%', sm: 160 } }}
        />
      </Stack>
      <TableContainer sx={{ px: 2, overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 400 }}>
          <TableHead>
            <TableRow>
              <TableCell>Query</TableCell>
              <TableCell align="right">Tokens</TableCell>
              <TableCell align="right">Cost</TableCell>
              <TableCell align="right">Time</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedQueries.map((q, i) => (
              <TableRow key={i}>
                <TableCell>
                  <Typography variant="body2" noWrap sx={{ maxWidth: { xs: 180, sm: 300, md: 400 } }}>
                    {q.query}
                  </Typography>
                </TableCell>
                <TableCell align="right">{fShortenNumber(q.tokens)}</TableCell>
                <TableCell align="right">${q.cost.toFixed(2)}</TableCell>
                <TableCell align="right">
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {new Date(q.timestamp).toLocaleString()}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={filteredQueries.length}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[5, 10, 25]}
      />
    </Card>
  );
}
